import apiSlice from '../apiSlice';
import { GlobalFilterKeys } from '../types';
import { stripGlobalFilterKeys } from '../utils';

import { ChangeLogData, ChangeLogsParams } from '@/features/changeLog';
import {
  AdGroupSearchParams,
  AdvertiserSearchParams,
  AlgorithmsData,
  ApplyRemoveAlgorithmRequestData,
  ApplyRemoveAlgorithmResponseData,
  CampaignSearchParams,
  InsertionOrderSearchParams,
  RawTraffickingAdGroupData,
  TraffickingAdGroupData,
  TraffickingAdvertiserData,
  TraffickingCampaignData,
  TraffickingInsertionOrderData,
} from '@/features/trafficking';
import { PaginationResponse } from '@/shared/utils/types';

const TRAFFICKING_API_URL = '/api/algorithms';

const traffickingTags = apiSlice.enhanceEndpoints({
  addTagTypes: [
    'advertiser',
    'campaigns',
    'adgroups',
    'insertionOrders',
    'changeLogs',
    'experiment',
  ],
});

const traffickingApiSlice = traffickingTags.injectEndpoints({
  endpoints: (builder) => ({
    getAlgorithms: builder.query<AlgorithmsData[], null>({
      query: () => ({
        url: TRAFFICKING_API_URL,
        method: 'GET',
      }),
    }),

    getTraffickingAdvertisers: builder.query<
      PaginationResponse<TraffickingAdvertiserData>,
      GlobalFilterKeys & AdvertiserSearchParams
    >({
      query: (params) => ({
        url: `${TRAFFICKING_API_URL}/advertisers/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      providesTags: [{ type: 'advertiser', id: 'LIST' }],
    }),

    getTraffickingCampaign: builder.query<
      PaginationResponse<TraffickingCampaignData>,
      GlobalFilterKeys &
        CampaignSearchParams & { mediaPlan?: number; osoMediaPlan?: number }
    >({
      query: (params) => ({
        url: `${TRAFFICKING_API_URL}/campaigns/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      providesTags: [{ type: 'campaigns', id: 'LIST' }],
    }),

    getTraffickingAdGroup: builder.query<
      PaginationResponse<TraffickingAdGroupData>,
      GlobalFilterKeys & AdGroupSearchParams
    >({
      query: (params) => ({
        url: `${TRAFFICKING_API_URL}/adgroups/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      transformResponse: (
        response: PaginationResponse<RawTraffickingAdGroupData>
      ) => {
        const { results, ...pagination } = response;

        const normalizedResults = results.map((adGroup) => {
          const { adSetId, adSetName, adGroupId, adGroupName, ...other } =
            adGroup;

          return {
            ...other,
            adGroupId: adGroupId ?? adSetId ?? '',
            adGroupName: adGroupName ?? adSetName ?? '',
          };
        });

        return { results: normalizedResults, ...pagination };
      },
      providesTags: [{ type: 'adgroups', id: 'LIST' }],
    }),

    applyRemoveAlgorithms: builder.mutation<
      ApplyRemoveAlgorithmResponseData,
      ApplyRemoveAlgorithmRequestData
    >({
      query: (data) => ({
        url: `${TRAFFICKING_API_URL}/manage-algorithms/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { mediaPlan }) => [
        { type: 'advertiser', id: 'LIST' },
        { type: 'campaigns', id: 'LIST' },
        { type: 'adgroups', id: 'LIST' },
        { type: 'insertionOrders', id: 'LIST' },
        { type: 'changeLogs', id: 'LIST' },
        ...(mediaPlan != null
          ? [{ type: 'experiment' as const, id: mediaPlan }]
          : []),
      ],
    }),

    getTraffickingInsertionOrders: builder.query<
      PaginationResponse<TraffickingInsertionOrderData>,
      GlobalFilterKeys & InsertionOrderSearchParams
    >({
      query: (params) => ({
        url: `${TRAFFICKING_API_URL}/insertion-orders/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      providesTags: [{ type: 'insertionOrders', id: 'LIST' }],
    }),

    getChangeLogs: builder.query<
      PaginationResponse<ChangeLogData>,
      GlobalFilterKeys & ChangeLogsParams
    >({
      query: (params) => ({
        url: `${TRAFFICKING_API_URL}/change-logs/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      providesTags: [{ type: 'changeLogs', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAlgorithmsQuery,
  useGetTraffickingAdvertisersQuery,
  useGetTraffickingAdGroupQuery,
  useGetTraffickingCampaignQuery,
  useGetTraffickingInsertionOrdersQuery,
  useApplyRemoveAlgorithmsMutation,
  useGetChangeLogsQuery,
} = traffickingApiSlice;
